/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ROUTES } from '../../../constants/routes';
import { generateUrl } from '../../util';
import { DATE, formatDate } from '../../../utils/date';
import { generateReferenceUtrId, loadReferenceUtr, referenceUtrSelectors } from '../../../slice/referenceUtrSlice';
import { getName } from '../../../utils/universalTracker';
import { useAppSettings } from '../../../hooks/app/useAppSettings';
import SimpleTooltip from '../../../components/simple-tooltip';
import { DataPeriods } from '../../../types/universalTracker';
import { getLatestReportData, getSurveyId, getUtrv } from './utils/selectors';
import { InitiativeUTRLookup } from '../../../types/initiative';
import { QUESTION } from '../../../constants/terminology';

export interface QuestionReferenceProps {
  utrCodes?: string[];
  showNoData: boolean;
  width?: string | number;
}

type LinkText = string | React.JSX.Element;

interface SurveyModalProps {
  linkText: LinkText;
  modalText: string;
  actionText: string;
  actionUrl: string;
  className?: string;
}

const SurveyModal = (props: SurveyModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const history = useHistory();

  return (
    <>
      <Button size={'sm'} className={`p-0 text-left ${props.className ?? ''}`} color='link' onClick={() => setIsOpen(!isOpen)}>
        {props.linkText ?? ''}
      </Button>
      <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} backdrop='static'>
        <ModalHeader toggle={() => setIsOpen(!isOpen)}>Action</ModalHeader>
        <ModalBody>
          <p>{props.modalText}</p>
          <div className='text-right'>
            <Button color='link-secondary' className='mr-3' onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button color='primary' onClick={() => history.push(props.actionUrl)}>{props.actionText}</Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

interface SurveyLinkProps {
  text: LinkText,
  initiativeId: string | undefined,
  surveyId?: string,
  utrv?: InitiativeUTRLookup['utrv'];
  className?: string;
}

const SurveyLink = (props: SurveyLinkProps) => {
  const { initiativeId, surveyId, text, utrv, className = '' } = props;
  const surveyModalStyle = `text-truncate d-inline-block ${className}`;

  const history = useHistory();
  if (!initiativeId) {
    return <>{text}</>;
  }

  if (!surveyId) {
    return <SurveyModal
      className={surveyModalStyle}
      linkText={text}
      modalText={`There are no surveys for the required period or ${QUESTION.SINGULAR} is not in scope.`}
      actionText={'Create a survey'}
      actionUrl={`${generateUrl(ROUTES.COMPANY_TRACKER_LIST, { initiativeId })}/create`}
    />
  }

  const utrvId = utrv?._id;
  if (!utrvId) {
    return <SurveyModal
      className={surveyModalStyle}
      linkText={text}
      modalText={`The selected survey does not have this ${QUESTION.SINGULAR} in Scope`}
      actionText={'Update survey scope'}
      actionUrl={generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: 'scope' })}
    />
  }

  const rootUrl = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: 'question' });
  const url = `${rootUrl}/${utrvId}`;

  return (
    <Button onClick={() => history.push(url)} color='link' className={`text-truncate p-0 ${className}`}>
      {text}
    </Button>
  );
}

const ReferenceTitle = (props: { isPortfolioTracker: boolean, effectiveDate?: string }) => {
  if (!props.effectiveDate) {
    if (props.isPortfolioTracker) {
      return <div>There is no company data.</div>;
    }
    return <div>To see data, answer:</div>;
  }

  return <div>Taken from: {formatDate(props.effectiveDate, DATE.MONTH_YEAR, true)}</div>;
}

interface ReferenceLinksParams {
  utrCodes: string[],
  initiativeId: string,
  period?: DataPeriods,
  getName?: (utrCode: string) => LinkText;
  className?: string;
  tooltipText?: LinkText;
}

export const ReferenceLinks = (props: ReferenceLinksParams) => {
  const { utrCodes, initiativeId, period, className = '', tooltipText } = props;
  const utrEntries = useAppSelector(referenceUtrSelectors.selectEntities);
  const { settingsRecommendedAddons: defaultAlternativeCodes } = useAppSettings();

  const historicalReportData = useAppSelector(getLatestReportData);
  const surveyId = useAppSelector(state => state.insightsData.surveyId) ?? getSurveyId(historicalReportData);

  const findUtrv = (utrCode: string): InitiativeUTRLookup['utrv'] => {
    const utrv = getUtrv(historicalReportData, utrCode);
    if (utrv) {
      return utrv;
    }

    if (!surveyId) {
      const utrId = generateReferenceUtrId({ initiativeId, utrCode, period })
      const utrLookup = utrEntries[utrId]?.data;
      return utrLookup?.utrv;
    }
  }

  const getUtrName = (utrCode: string) => {
    // Allow name override by custom function
    if (typeof props.getName === 'function') {
      return props.getName(utrCode);
    }
    const utrId = generateReferenceUtrId({ initiativeId, utrCode, period })
    const utrLookup = utrEntries[utrId]?.data;
    return utrLookup?.utr ? getName(utrLookup?.utr, defaultAlternativeCodes) : utrCode;
  }

  if (utrCodes.length === 1) {
    const utrCode = utrCodes[0];
    const utrName = getUtrName(utrCode);
    const utrv = findUtrv(utrCode);
    return (
      <div className={`chart-message-utr d-inline-block ${className}`} key={`utr_${utrCode}`}>
        <SimpleTooltip text={tooltipText ?? utrName}>
          <SurveyLink
            text={utrName}
            initiativeId={initiativeId}
            surveyId={surveyId}
            className={className}
            utrv={utrv}
          />
        </SimpleTooltip>
      </div>
    );
  }

  const length = utrCodes.length;
  return (
    <>
      <span className='mr-2'>Multiple {QUESTION.PLURAL}:</span>
      {utrCodes.map((utrCode, ix) => {
        const utrName = getUtrName(utrCode);
        const utrv = findUtrv(utrCode);
        return <Fragment key={`utr_${utrCode}-${ix}`}>
          <span className={`chart-message-utr ${className}`}>
            <SimpleTooltip text={tooltipText ?? utrName}>
              <SurveyLink text={`[${ix + 1}]`} className={className} initiativeId={initiativeId} surveyId={surveyId} utrv={utrv} />
            </SimpleTooltip>
          </span>
          {ix < length - 1 ? <span>, </span> : null}
        </Fragment>;
      })}
    </>
  );
}

export const QuestionReference = (props: QuestionReferenceProps) => {
  const { utrCodes, showNoData } = props;

  const dispatch = useAppDispatch();
  // @TODO Refactor and remove dependency on route params
  const { initiativeId, companyId } = useParams<{ initiativeId?: string, companyId?: string }>();

  const utrEntries = useAppSelector(referenceUtrSelectors.selectEntities);
  const historicalReportData = useAppSelector(getLatestReportData);
  const period = useAppSelector(state => state.insightsData.cachedPeriod);
  const surveyId = useAppSelector(state => state.insightsData.surveyId) ?? getSurveyId(historicalReportData);

  const id = initiativeId ?? companyId;
  const isPortfolioTracker = Boolean(companyId);

  useEffect(() => {
    utrCodes?.forEach(utrCode => {
      // Only display data from historicalReportData in PT, outside data is not available
      if (id && !isPortfolioTracker) {
        dispatch(loadReferenceUtr({ initiativeId: id, utrCode, period }))
      }
    })
  }, [utrCodes, dispatch, id, period, isPortfolioTracker]);

  const findUtrv = useCallback((utrCode: string): InitiativeUTRLookup['utrv'] => {
    if (!id) {
      return;
    }

    const utrv = getUtrv(historicalReportData, utrCode);
    if (utrv) {
      return utrv;
    }

    if (!surveyId) {
      const utrId = generateReferenceUtrId({ initiativeId: id, utrCode, period })
      const utrLookup = utrEntries[utrId]?.data;
      return utrLookup?.utrv;
    }
  }, [id, period, surveyId, utrEntries, historicalReportData]);

  const effectiveDate = useMemo(() => {
    if (!utrCodes || showNoData) {
      return;
    }
    let utrv: { effectiveDate: string } | undefined;
    for (let utrCode of utrCodes) {
      const found = findUtrv(utrCode);
      if (!found) {
        continue;
      }
      utrv = found;
      break;
    }
    return utrv?.effectiveDate;
  }, [utrCodes, findUtrv, showNoData]);

  return utrCodes ? (
    <div
      className='insights-question-reference mt-1 px-3'
      style={props.width ? { width: `${props.width}px` } : undefined}
    >
      {id && utrCodes && utrCodes.length ? (
        <div className='chart-message'>
          <ReferenceTitle effectiveDate={effectiveDate} isPortfolioTracker={isPortfolioTracker} />
          {isPortfolioTracker ? null : <ReferenceLinks utrCodes={utrCodes} initiativeId={id} period={period} />}
        </div>
      ) : null}
    </div>
  ) : null;
}
